import { khoangNgayVn, ngayDayVn } from '@qlk/shared';
import { Check, ChevronDown } from 'lucide-react';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import {
  cacMaKy,
  cuoiThang,
  kyNgoaiMocDuLieu,
  luiNgay,
  MA_KY_LAN,
  MA_MO_NAM_KHAC,
  MA_MO_TUY_CHINH,
  maTuyChinh,
  SO_NAM_HIEN,
  type KyTrang,
  type MocChonKy,
} from '../lib/kyTrang.js';
import { HopThoai } from './HopThoai.js';
import { Nut } from './Nut.js';
import { StatusBadge } from './StatusBadge.js';
import { O } from './Truong.js';

/**
 * Ô chọn kỳ **dùng chung** — thanh trên của Tổng quan / Kênh / Network / Hiệu
 * suất, và đầu trang chi tiết kênh.
 *
 * Một component chứ không năm: trước đợt 23/10/2026 mỗi trang có một bộ chọn
 * riêng (ô 5 preset ở thanh trên, ô 8 dạng ở trang kênh, Network thì không có
 * ô nào), nên cùng một câu hỏi "đang xem kỳ nào" có ba câu trả lời khác nhau
 * và chuyển trang là mất kỳ.
 *
 * Mục "Tuỳ chỉnh…" **không phải một kỳ**: chọn nó là mở hộp hai ô ngày, kèm
 * tám lối tắt (7/28/90/365 ngày, tháng này, tháng trước, quý này, năm nay).
 * Lối tắt chỉ **điền** vào hai ô chứ không áp luôn, để người dùng thấy khoảng
 * mình sắp chọn trước khi bấm.
 */
export interface ChonKyProps {
  ky: KyTrang;
  moc: MocChonKy;
  onChon: (ma: string) => void;
  /**
   * Nhãn chữ bên trái ô (`Kỳ của cả trang`). Thanh trên không cần — ô nằm
   * cạnh tên trang nên đã rõ nó điều khiển cái gì.
   */
  nhanTrai?: string;
  /**
   * In khoảng ngày thật cạnh ô (`23/09 – 20/10/2026`).
   *
   * Thanh trên **không** in: chỗ đó chật, và dòng phụ dưới tiêu đề mỗi trang
   * đã ghi đúng khoảng đó.
   */
  hienKhoang?: boolean;
}

/** Một lối tắt của hộp tuỳ chỉnh: nhãn + khoảng ngày nó điền vào hai ô. */
function cacLoiTat(ngayCuoi: string): { nhan: string; from: string; to: string }[] {
  const thangNay = ngayCuoi.slice(0, 7);
  const thangTruoc = `${thangNay}-01`;
  const dauThangTruoc = luiNgay(thangTruoc, 1).slice(0, 7);
  const nam = ngayCuoi.slice(0, 4);
  const thangSo = Number(ngayCuoi.slice(5, 7));
  const dauQuy = `${nam}-${String(Math.floor((thangSo - 1) / 3) * 3 + 1).padStart(2, '0')}`;

  const boTheoMoc = (to: string) => (to < ngayCuoi ? to : ngayCuoi);

  return [
    ...Object.entries(MA_KY_LAN).map(([, soNgay]) => ({
      nhan: `${String(soNgay)} ngày`,
      from: luiNgay(ngayCuoi, soNgay - 1),
      to: ngayCuoi,
    })),
    { nhan: 'Tháng này', from: `${thangNay}-01`, to: boTheoMoc(cuoiThang(thangNay)) },
    {
      nhan: 'Tháng trước',
      from: `${dauThangTruoc}-01`,
      to: cuoiThang(dauThangTruoc),
    },
    { nhan: 'Quý này', from: `${dauQuy}-01`, to: ngayCuoi },
    { nhan: 'Năm nay', from: `${nam}-01-01`, to: boTheoMoc(`${nam}-12-31`) },
  ];
}

function HopTuyChinh({
  ky,
  ngayCuoi,
  onDong,
  onChon,
}: {
  ky: KyTrang;
  ngayCuoi: string;
  onDong: () => void;
  onChon: (ma: string) => void;
}) {
  const [tu, datTu] = useState(ky.from === '' ? ngayCuoi : ky.from);
  const [den, datDen] = useState(ky.to === '' ? ngayCuoi : ky.to);

  /*
   * Ba luật, kiểm ở đây chứ không chờ máy chủ: khoảng ngược là khoảng không
   * có số nào, còn ngày sau mốc dữ liệu là xin YouTube những ngày chưa tồn
   * tại (và biểu đồ hiện mấy cột 0 ở cuối).
   */
  const loi =
    tu === '' || den === ''
      ? 'Chọn cả ngày bắt đầu và ngày kết thúc.'
      : tu > den
        ? 'Ngày bắt đầu phải trước hoặc bằng ngày kết thúc.'
        : ngayCuoi !== '' && den > ngayCuoi
          ? `Số liệu mới có tới ${ngayDayVn(ngayCuoi)}. Chọn ngày kết thúc sớm hơn.`
          : null;

  return (
    <HopThoai
      title="Chọn khoảng ngày"
      rong="sm"
      onDong={onDong}
      chanHop={
        <div className="flex justify-end gap-2">
          <Nut kieu="phu" onClick={onDong}>
            Huỷ
          </Nut>
          <Nut
            kieu="chinh"
            disabled={loi !== null}
            onClick={() => {
              onChon(maTuyChinh(tu, den));
              onDong();
            }}
          >
            Áp dụng
          </Nut>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <O label="Từ ngày" type="date" value={tu} onChange={datTu} max={den} />
          <O
            label="Đến ngày"
            type="date"
            value={den}
            onChange={datDen}
            min={tu}
            {...(ngayCuoi === '' ? {} : { max: ngayCuoi })}
          />
        </div>

        <div>
          <p className="mb-2 text-phu font-medium text-chu-phu">Lối tắt</p>
          <div className="flex flex-wrap gap-2">
            {cacLoiTat(ngayCuoi === '' ? den : ngayCuoi).map((l) => (
              <button
                key={l.nhan}
                type="button"
                onClick={() => {
                  datTu(l.from);
                  datDen(l.to);
                }}
                className="vung-bam rounded-o border border-vien bg-be-mat px-3 text-co-ban text-chu hover:bg-neutral-nen"
              >
                {l.nhan}
              </button>
            ))}
          </div>
        </div>

        {loi === null ? (
          <p className="so-lieu text-co-ban text-chu-phu">
            Kỳ sẽ là <strong className="text-chu">{khoangNgayVn(tu, den)}</strong>
          </p>
        ) : (
          <p className="text-co-ban text-bad-chu">{loi}</p>
        )}

        <p className="text-phu text-chu-phu">
          Khoảng không trùng trọn một tháng thì số tiền là <strong>tạm tính</strong>: sao kê
          Network, biên bản chia và đối soát nhân sự đều chốt theo tháng.
        </p>
      </div>
    </HopThoai>
  );
}

/**
 * Menu kỳ **tự vẽ** thay cho `<select>`.
 *
 * Vì sao không dùng `<select>` nữa: danh sách có bốn nhóm, và kênh chạy nhiều
 * năm thì nó dài quá một màn hình — người dùng phải cuộn trong một hộp không
 * thấy được vạch ngăn nhóm nào. Ở đây nhóm có nhãn và vạch ngăn thật, và quá
 * `SO_NAM_HIEN` năm thì phần năm cũ gập lại sau mục "Năm khác…".
 *
 * Đổi lại phải tự làm những thứ `<select>` cho sẵn, nên đừng gỡ bớt:
 * `role="listbox"` + `aria-activedescendant` cho trình đọc màn hình, mũi tên /
 * Home / End / Enter / Escape cho bàn phím, đóng khi bấm ra ngoài, và trả nét
 * focus về nút khi đóng.
 */
function MenuChonKy({
  ky,
  moc,
  nhan,
  onChon,
  onMoTuyChinh,
}: {
  ky: KyTrang;
  moc: MocChonKy;
  nhan: string;
  onChon: (ma: string) => void;
  onMoTuyChinh: () => void;
}) {
  const [mo, datMo] = useState(false);
  /* Kỳ đang xem là một năm cũ (dán từ link) thì mở sẵn phần năm khác — nếu
     không, mục đang chọn nằm trong một danh sách đã gập. */
  const namCu = ky.loai === 'nam' && moc.cacNam.indexOf(ky.ma) >= SO_NAM_HIEN;
  const [moNamKhac, datMoNamKhac] = useState(false);
  const [dangTro, datDangTro] = useState(ky.ma);

  const boc = useRef<HTMLDivElement>(null);
  const nutRef = useRef<HTMLButtonElement>(null);
  const hopRef = useRef<HTMLDivElement>(null);
  const idGoc = useId();

  const muc = cacMaKy(moc, ky, moNamKhac || namCu);
  const idMuc = (value: string) => `${idGoc}-${value}`;

  const dong = (traFocus = true) => {
    datMo(false);
    if (traFocus) nutRef.current?.focus();
  };

  useEffect(() => {
    if (!mo) return;

    const bamNgoai = (e: MouseEvent) => {
      if (boc.current && !boc.current.contains(e.target as Node)) datMo(false);
    };
    document.addEventListener('mousedown', bamNgoai);
    return () => {
      document.removeEventListener('mousedown', bamNgoai);
    };
  }, [mo]);

  /* Mở ra thì nét focus vào hộp (để bắt được mũi tên) và trỏ vào mục đang chọn. */
  useEffect(() => {
    if (!mo) return;
    datDangTro(ky.ma);
    hopRef.current?.focus();
  }, [mo, ky.ma]);

  const chon = (value: string) => {
    if (value === MA_MO_NAM_KHAC) {
      /* Không đóng: bấm "Năm khác…" là để **xem thêm**, chọn kỳ là bước sau. */
      datMoNamKhac(true);
      datDangTro(moc.cacNam[SO_NAM_HIEN] ?? value);
      return;
    }
    dong();
    if (value === MA_MO_TUY_CHINH) onMoTuyChinh();
    else onChon(value);
  };

  const diChuyen = (buoc: number) => {
    const i = muc.findIndex((m) => m.value === dangTro);
    const tiep = Math.min(Math.max((i < 0 ? 0 : i) + buoc, 0), muc.length - 1);
    const m = muc[tiep];
    if (m) datDangTro(m.value);
  };

  const banPhim = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const phim = e.key;

    if (phim === 'Escape') {
      e.preventDefault();
      dong();
      return;
    }
    /* Tab đóng menu nhưng **không** chặn: nét focus đi tiếp như thường. */
    if (phim === 'Tab') {
      dong(false);
      return;
    }
    if (phim === 'ArrowDown' || phim === 'ArrowUp') {
      e.preventDefault();
      diChuyen(phim === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (phim === 'Home' || phim === 'End') {
      e.preventDefault();
      const m = phim === 'Home' ? muc[0] : muc[muc.length - 1];
      if (m) datDangTro(m.value);
      return;
    }
    if (phim === 'Enter' || phim === ' ') {
      e.preventDefault();
      chon(dangTro);
    }
  };

  return (
    <div ref={boc} className="relative min-w-0">
      <button
        ref={nutRef}
        type="button"
        aria-label={nhan}
        aria-haspopup="listbox"
        aria-expanded={mo}
        /* Mã kỳ đang chọn, cho bài e2e: nút không phải `<select>` nên không có
           `value` nào để `toHaveValue` đọc. */
        data-ky-dang-chon={ky.ma}
        onClick={() => {
          datMo((x) => !x);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            datMo(true);
          }
        }}
        className="vung-bam flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-o border border-vien bg-be-mat px-3 text-co-ban font-medium text-chu hover:bg-neutral-nen sm:w-auto"
      >
        <span className="min-w-0 truncate">{ky.nhan}</span>
        <ChevronDown size={16} aria-hidden="true" className="shrink-0 text-chu-phu" />
      </button>

      {mo && (
        <div
          ref={hopRef}
          role="listbox"
          tabIndex={-1}
          aria-label={nhan}
          aria-activedescendant={idMuc(dangTro)}
          onKeyDown={banPhim}
          className="absolute left-0 z-30 mt-1 max-h-[min(70vh,420px)] min-w-[220px] overflow-y-auto rounded-o border border-vien bg-be-mat py-1 shadow-lg"
        >
          {muc.map((m, i) => {
            const nhomMoi = i === 0 || muc[i - 1]?.nhom !== m.nhom;
            return (
              /* `role="presentation"`: ARIA đòi `option` thuộc **trực tiếp** về
                 `listbox`, mà đây là thẻ bọc chỉ để gom vạch ngăn + nhãn nhóm
                 với mục. Không khai thì cây trợ năng thấy một thẻ lạ chen giữa
                 và trình đọc màn hình bỏ qua cả mục. */
              <div key={m.value} role="presentation">
                {nhomMoi && i > 0 && (
                  <div role="presentation" className="my-1 border-t border-vien" />
                )}
                {nhomMoi && (
                  <div
                    role="presentation"
                    className="px-3 pt-1 pb-0.5 text-phu font-medium text-chu-mo"
                  >
                    {m.nhom}
                  </div>
                )}
                <button
                  type="button"
                  id={idMuc(m.value)}
                  role="option"
                  aria-selected={m.value === ky.ma}
                  data-ky={m.value}
                  data-nhom-ky={m.nhom}
                  onClick={() => {
                    chon(m.value);
                  }}
                  onMouseEnter={() => {
                    datDangTro(m.value);
                  }}
                  className={`vung-bam flex w-full items-center justify-between gap-3 px-3 text-left text-co-ban ${
                    m.value === dangTro ? 'bg-neutral-nen' : ''
                  } ${m.value === ky.ma ? 'font-medium text-nhan' : 'text-chu'}`}
                >
                  <span className="min-w-0 truncate">{m.label}</span>
                  {m.value === ky.ma && <Check size={16} aria-hidden="true" className="shrink-0" />}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ChonKy({ ky, moc, onChon, nhanTrai, hienKhoang = false }: ChonKyProps) {
  const [moHop, datMoHop] = useState(false);

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {nhanTrai !== undefined && <span className="text-phu text-chu-phu">{nhanTrai}</span>}

      <MenuChonKy
        ky={ky}
        moc={moc}
        nhan={nhanTrai ?? 'Kỳ số liệu'}
        onChon={onChon}
        onMoTuyChinh={() => {
          datMoHop(true);
        }}
      />

      {kyNgoaiMocDuLieu(ky, moc) && (
        /* Kỳ ngoài mốc dữ liệu: link cũ (`?ky=2017`) hay link của người có phạm
           vi rộng hơn vẫn mở được, nhưng phải nói ra là không có số nào — thay
           vì để người đọc tưởng kênh tụt về 0. */
        <span className="text-phu text-chu-phu">Không có số liệu trong kỳ này</span>
      )}

      {hienKhoang && ky.nhanKhoang !== '' && (
        /* `whitespace-nowrap`: một khoảng ngày gãy làm hai dòng đọc ra hai ngày
           khác (bài e2e 92 canh luật này cho `.so-lieu`). */
        <span className="so-lieu text-phu whitespace-nowrap text-chu-mo">{ky.nhanKhoang}</span>
      )}

      {ky.tamTinh && (
        <StatusBadge tone="warn" cham={false}>
          <span
            title={
              ky.loai === 'nam'
                ? 'Năm chưa kết thúc: số còn chạy tới hết năm.'
                : 'Kỳ chưa kết thúc: số còn chạy tới hết tháng và chưa đối soát với sao kê Network.'
            }
          >
            tạm tính
          </span>
        </StatusBadge>
      )}

      {moHop && (
        <HopTuyChinh
          ky={ky}
          ngayCuoi={moc.ngayCuoi}
          onChon={onChon}
          onDong={() => {
            datMoHop(false);
          }}
        />
      )}
    </div>
  );
}
